import React, { memo, Suspense, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader, Checkbox,
    Container,
    Grid,
    IconButton,
    Link,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import { Add, Carpenter, ContentCut, Delete, Edit, LinkOff, MoreVert } from '@mui/icons-material';
import Date from 'components/formaters/Date';
import useContextMenu from 'hooks/useContextMenu';
import useCheckboxList from 'hooks/useCheckboxList';
import { BLModel } from 'models/BLModel';
import { VoyageModel } from 'models/VoyageModel';
import { PartyType } from 'models/PartyModel';
import useBLs from 'modules/voyages/hooks/useBLs';
import useVoyages from 'modules/voyages/hooks/useVoyages';
import useBLModals from 'modules/voyages/hooks/useBLModals';
import useBLFilter from 'modules/voyages/hooks/useBLFilter';
import useVoyageDocs from 'modules/voyages/hooks/useVoyageDocs';
import usePartyModals from 'modules/parties/hooks/usePartyModals';
import auth from 'recoil/atoms/auth';
import blTypeList from 'recoil/atoms/BLTypeList';
import { arrayGrouped, interpolate, replaceItemAtIndex } from 'utils/common';


const splits = [
    'dis_car_no_frame',
    'dis_car_frame',
    'dis_truck_frame',
    'cut',
    'dis_excavator',
];

const Inner = () => {
    const {t} = useTranslation('cars');
    const {id} = useParams();
    const my = useRecoilValue(auth);
    const navigate = useNavigate();
    const [voyage, setVoyage] = useState<VoyageModel>();
    const {
        load,
        bls,
        setBls,
        editable,
        setEditable,
        filter,
        changeFilter,
        shipperFilterOptions,
        consigneeFilterOptions,
        notifyFilterOptions,
    } = useBLFilter(Number(id));
    const {checkedAll, checkedSome, checked, checkOne, checkAll, countChecked, getItems, uncheckAll} = useCheckboxList(bls);
    const {getVoyage} = useVoyages();
    const blTypes = useRecoilValue(blTypeList);
    const {updateBL, splitBL, normalizeNumbers} = useBLs();
    const blModals = useBLModals();
    const partyModals = usePartyModals();
    const {downloadManifest, downloadBLs} = useVoyageDocs();

    // eslint-disable-next-line
    useEffect(() => {
        getVoyage(Number(id))
            .then(data => setVoyage(data.data))
            .catch(() => navigate('/voyages'));
        load();
    }, [id]);

    const types = useMemo(() => arrayGrouped(blTypes, 'type'), []);

    const {openMenu: openSplitMenu} = useContextMenu<BLModel>(({close, value}) => splits.map(type => (
        <MenuItem
            key={type}
            onClick={() => {
                splitBL(value.id, type).then(load);
                close();
            }}
        >
            {t(type)}
        </MenuItem>
    )));

    const {openMenu: openFillMenu} = useContextMenu<BLModel>(({close, value}) => types[value.type]?.map(type => (
        <MenuItem
            key={type.id}
            onClick={() => {
                setEditable(old => {
                    const i = old.findIndex(bl => bl.id === value.id);
                    return replaceItemAtIndex(old, i, {
                        ...old[i],
                        description: interpolate(type.description, value.type === 'cargo' ? value.innerCargo : value.car)
                    });
                });
                close();
            }}
        >
            {type.name}
        </MenuItem>
    )));

    if (!voyage)
        return null;

    const mainAction = (
        <>
            <Button
                size="small"
                onClick={() => downloadManifest()(voyage.id)}
            >
                {t('ru_manifest')}
            </Button>
            {' '}
            <Button

                size="small"
                onClick={() => downloadBLs('ru')(voyage.id)}
            >
                {t('download')}
            </Button>
            {' '}
            <Button
                component={Link}
                size="small"
                href={`/bl/types?voyage=${voyage.id}`}
            >
                {t('bl_types')}
            </Button>
            {' '}

            <Button
                component={Link}
                size="small"
                href={`/voyage/${voyage.id}`}
            >
                {t('cars')}
            </Button>
            {' '}

            {my.permissions.has('invoices create') && (
                <Button
                    component={Link}
                    size="small"
                    href={`/voyage/${voyage.id}/finances`}
                >
                    {t('finances')}
                </Button>
            )}
        </>
    );

    const extractCargo = (bl: BLModel) => blModals.confirmExtractCargo(bl.innerCargo!).then(load);

    const deleteBL = (bl: BLModel) => blModals.confirmDeleteBL(bl).then(load);

    const addConsignee = (bl: BLModel) => partyModals.openAddPartyModal(
        carPartyTypes.includes(bl.type) ? 'car' : 'bl',
        carPartyTypes.includes(bl.type) ? bl.car!.id : bl.id,
        PartyType.Consignee,
        []
    ).then(load);

    const detachConsignee = (bl: BLModel) => partyModals.confirmDetachParty(
        carPartyTypes.includes(bl.type) ? 'car' : 'bl',
        carPartyTypes.includes(bl.type) ? bl.car!.id : bl.id,
        bl.consignee!
    ).then(load);

    const editConsignee = (bl: BLModel) => partyModals.openEditPartyModal(bl.consignee!).then(load);

    const assignShipper = (bls: BLModel[]) => blModals.openAssignShipperModal(bls).then(load);
    const editShipper = (bl: BLModel) => blModals.openEditShipperModal(bl).then(load);
    const removeShipper = (bl: BLModel) => blModals.confirmDeleteShipper(bl).then(load);

    const update = (i: number, name: string, value: string) =>
        setEditable(old => replaceItemAtIndex(old, i, {...old[i], [name]: value}));

    const save = () => {
        updateBL(editable);
        setBls(editable);
        setEditable([]);
    };

    const edit = editable.length > 0;

    const filterInputs = [];
    if (shipperFilterOptions.length)
        filterInputs.push(
            <TextField
                select
                id="shipper"
                key="shipper"
                name="shipper"
                label={t('shipper')}
                fullWidth
                variant="outlined"
                value={filter.shipper === undefined ? '' : filter.shipper}
                onChange={e => changeFilter(e.target.name, e.target.value)}
            >
                {shipperFilterOptions.map(({value, label}) =>
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                )}
            </TextField>
        );

    if (consigneeFilterOptions.length)
        filterInputs.push(
            <TextField
                select
                id="consignee"
                key="consignee"
                name="consignee"
                label={t('consignee')}
                fullWidth
                variant="outlined"
                value={filter.consignee === undefined ? '' : filter.consignee}
                onChange={e => changeFilter(e.target.name, e.target.value)}
            >
                {consigneeFilterOptions.map(({value, label}) =>
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                )}
            </TextField>
        );

    if (notifyFilterOptions.length)
        filterInputs.push(
            <TextField
                select
                id="notify"
                key="notify"
                name="notify"
                label={t('notify')}
                fullWidth
                variant="outlined"
                value={filter.notify === undefined ? '' : filter.notify}
                onChange={e => changeFilter(e.target.name, e.target.value)}
            >
                {notifyFilterOptions.map(({value, label}) =>
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                )}
            </TextField>
        );

    return (
        <Container maxWidth={false} sx={{mt: 2}}>
            <Card sx={{mb: 2}}>
                <CardHeader
                    title={`${t('voyage')} ${voyage.vessel.name} ${voyage.number}`}
                    action={mainAction}
                />
                <CardContent>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={12} sm={4} display="flex" justifyContent="space-between">
                            <Box>
                                {voyage.pol.name} <br/>
                                <small><Date date={voyage.departedAt || voyage.etd}/></small>
                            </Box>
                            <Box textAlign="center" textTransform="uppercase">
                                {voyage.status}
                            </Box>
                            <Box textAlign="right">
                                {voyage.pod.name} <br/>
                                <small><Date date={voyage.arrivedAt || voyage.eta}/></small>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={5}>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={2} sx={{mb: 2}}>
                {filterInputs.map((children, i) => (
                    <Grid key={i} item xs={12} sm={2}>
                        {children}
                    </Grid>
                ))}
                <Grid item sx={{marginLeft: 'auto'}}>
                    {countChecked > 0 && (
                        <>
                            <span>{t('with_selected')}:</span>
                            {' '}
                            <Button
                                size="small"
                                onClick={() => assignShipper(getItems())}
                            >
                                {t('set_shipper')}
                            </Button>
                        </>
                    )}
                </Grid>
            </Grid>

            <TableContainer>
                <Table
                    size="small"
                    sx={{
                        mb: 8,

                        '& .show-on-hover': {
                            visibility: 'hidden',
                        },
                        'tbody tr:hover': {
                            backgroundColor: 'action.hover',
                            '& .show-on-hover': {
                                visibility: 'visible',
                            },
                        },
                        '.MuiTableCell-sizeSmall': {
                            padding: edit ? 0 : '6px',
                            '&:first-of-type': {
                                paddingLeft: 2,
                            }
                        },

                        '& .pad': {
                            padding: '0 6px',
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell width={50}>
                                <Checkbox
                                    checked={checkedAll}
                                    indeterminate={checkedSome}
                                    onChange={checkAll}
                                />
                            </TableCell>
                            <TableCell width={50}>#</TableCell>
                            <TableCell width={170}>{t('number')}</TableCell>
                            <TableCell width={50}>{t('pos')}</TableCell>
                            <TableCell>{t('shipper')}</TableCell>
                            <TableCell>{t('consignee')}</TableCell>
                            <TableCell>{t('notify')}</TableCell>
                            <TableCell>{t('type')}</TableCell>
                            <TableCell width="20%">{t('description')}</TableCell>
                            <TableCell>{t('chassis')}</TableCell>
                            <TableCell>{t('engine_number')}</TableCell>
                            <TableCell align="right">{t('qty')}</TableCell>
                            <TableCell width={90} align="right">{t('weight')}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(edit ? editable : bls).map((bl, i) => (
                            <BLRow
                                key={`${bl.id} ${bl.innerCargo?.id || i}`}
                                checked={checked[i]}
                                checkOne={checkOne}
                                bl={bl}
                                i={i}
                                openSplit={openSplitMenu}
                                openFill={openFillMenu}
                                extractCargo={extractCargo}
                                deleteBL={deleteBL}
                                addConsignee={addConsignee}
                                editConsignee={editConsignee}
                                detachConsignee={detachConsignee}
                                update={update}
                                edit={edit}
                                editShipper={editShipper}
                                removeShipper={removeShipper}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    position: 'fixed',
                    bottom: theme => theme.spacing(2),
                    right: theme => theme.spacing(5)
                }}
            >
                {edit ? (
                    <>
                        <Button
                            onClick={() => setEditable([])}
                            variant="contained"
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            sx={{ml: 5}}
                            onClick={save}
                            variant="contained"
                            color="success"
                        >
                            {t('save')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={() => blModals.openGenerateNumbersModal(voyage.id).then(load)}
                            variant="contained"
                        >
                            {t('generate_numbers')}
                        </Button>
                        <Button
                            sx={{ml: 5}}
                            onClick={() => normalizeNumbers(voyage.id).then(load)}
                            variant="contained"
                        >
                            {t('normalize_numbers')}
                        </Button>
                        <Button
                            sx={{ml: 5}}
                            onClick={() => setEditable(bls)}
                            variant="contained"
                        >
                            {t('edit')}
                        </Button>
                    </>
                )}

            </Box>
        </Container>
    );
};

const carPartyTypes = ['whole', 'cabin', 'fullbody', 'body', 'front', 'excavator_frame'];
const chassisTypes = ['whole', 'frame', 'fullbody', 'front', 'excavator_frame'];
const engineTypes = ['whole', 'engine', 'engine_sus', 'front'];

const color = (type: string) => {
    const map = {
        whole: 'text.disabled',
        cargo: 'success.light',
        suspension: 'primary.light',
        engine: 'warning.light',
        engine_sus: 'warning.light',
        frame: 'info.light',
        rear: 'secondary.light',
        boom: 'error.light',
        boom_cylinders: 'error.light',
    } as Record<string, string>;

    return map[type] || undefined;
};


type BLRowProps = {
    bl: BLModel,
    update: (i: number, name: string, value: string) => void,
    i: number,
    openSplit: any,
    openFill: any,
    extractCargo: any,
    deleteBL: any,
    addConsignee: any,
    editConsignee: any,
    detachConsignee: any,
    edit: boolean,
    editShipper: any,
    removeShipper: any,
    checked: boolean,
    checkOne: (id: number, e: React.ChangeEvent<HTMLInputElement>) => void,
}

const BLRow = memo(({
                        bl,
                        i,
                        update,
                        openSplit,
                        openFill,
                        extractCargo,
                        deleteBL,
                        addConsignee,
                        editConsignee,
                        detachConsignee,
                        edit,
                        editShipper,
                        removeShipper,
                        checked,
                        checkOne,
                    }: BLRowProps) => {

    return (
        <TableRow id={bl.id?.toString()}>
            <TableCell>
                {bl.id && (
                    <Checkbox
                        name={'ch-' + i}
                        checked={checked || false} // add false to keep "controlled" checked state between renders
                        onChange={e => checkOne(i, e)}
                    />
                )}
            </TableCell>
            <TableCell>{i + 1}</TableCell>

            <TableCell className={edit ? undefined : 'pad'}>
                {bl.id && (edit ? (
                    <TextField
                        margin="none"
                        variant="outlined"
                        fullWidth
                        value={bl.number || ''}
                        onChange={e => update(i, 'number', e.target.value)}
                    />
                ) : bl.number)}
            </TableCell>

            <TableCell className={edit ? undefined : 'pad'}>
                {bl.id && (edit ? (
                    <TextField
                        margin="none"
                        variant="outlined"
                        fullWidth
                        inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                        value={bl.pos || ''}
                        onChange={e => update(i, 'pos', e.target.value)}
                    />
                ) : bl.pos)}
            </TableCell>

            <TableCell className="pad">
                {bl.shipper.name}
                {' '}
                <IconButton
                    className="show-on-hover"
                    size="small"
                    onClick={() => editShipper(bl)}
                >
                    <Edit fontSize="small"/>
                </IconButton>
                {!bl.shipper.id && (
                    <IconButton
                        className="show-on-hover"
                        size="small"
                        onClick={() => removeShipper(bl)}
                    >
                        <Delete fontSize="small"/>
                    </IconButton>
                )}
            </TableCell>

            <TableCell>
                {bl.id && (
                    bl.consignee ? (
                        <>
                            {bl.consignee.name}
                            {' '}
                            <IconButton
                                className="show-on-hover"
                                size="small"
                                onClick={() => editConsignee(bl)}
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            <IconButton
                                className="show-on-hover"
                                size="small"
                                onClick={() => detachConsignee(bl)}
                            >
                                <LinkOff fontSize="small"/>
                            </IconButton>
                        </>
                    ) : (
                        <IconButton
                            size="small"
                            onClick={() => addConsignee(bl)}
                        >
                            <Add fontSize="small"/>
                        </IconButton>
                    ))}
            </TableCell>

            <TableCell className="pad">{bl.notify?.shortName}</TableCell>
            <TableCell className="pad"><Box color={color(bl.type)}>{bl.type}</Box></TableCell>
            <TableCell className={edit ? undefined : 'pad'} sx={{borderLeft: `2px solid ${bl.color}`}}>
                {bl.id && edit ? (
                    <Box position="relative">
                        <TextField
                            margin="none"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            value={bl.description || ''}
                            onChange={e => update(i, 'description', e.target.value)}
                        />
                        {bl.type !== 'cargo' && (
                            <IconButton
                                size="small"
                                sx={{position: 'absolute', top: 0, right: 0}}
                                onClick={e => openFill(e, bl)}
                            >
                                <MoreVert fontSize="small"/>
                            </IconButton>
                        )}
                    </Box>
                ) : bl.description
                }
            </TableCell>
            <TableCell className="pad">
                {chassisTypes.includes(bl.type) && bl.car && (
                    <Link href={`/car/${bl.car.id}`}>{bl.car?.chassisNumber}</Link>
                )}
            </TableCell>
            <TableCell className="pad">{engineTypes.includes(bl.type) && bl.car?.engineModel}</TableCell>
            <TableCell className="pad" align="right">{bl.quantity}</TableCell>
            <TableCell className={edit ? undefined : 'pad'} align="right">
                {edit ? (
                    <TextField
                        margin="none"
                        variant="outlined"
                        fullWidth
                        inputProps={{inputMode: 'numeric'}}
                        sx={{
                            input: {textAlign: 'right'},
                        }}
                        value={bl.weight || ''}
                        onChange={e => update(i, 'weight', e.target.value)}
                    />
                ) : bl.weight}
            </TableCell>

            <TableCell className="pad" align="right">
                {!bl.id && (
                    <IconButton
                        size="small"
                        onClick={() => extractCargo(bl)}
                    >
                        <ContentCut fontSize="small"/>
                    </IconButton>
                )}

                {bl.id && bl.type !== 'whole' && (
                    <IconButton
                        size="small"
                        onClick={() => deleteBL(bl)}
                    >
                        <Delete fontSize="small"/>
                    </IconButton>
                )}

                {bl.car?.exportType && bl.car?.exportType !== 'car' && bl.type === 'whole' &&
                    <IconButton
                        size="small"
                        onClick={e => openSplit(e, bl)}
                    >
                        <Carpenter fontSize="small"/>
                    </IconButton>}
            </TableCell>
        </TableRow>
    )
}, (prev, next) => (
    prev.bl === next.bl && prev.edit === next.edit && prev.checked === next.checked
));

const BL = () => {
    return (
        <Suspense fallback={null}>
            <Inner/>
        </Suspense>
    )
};

export default BL;
